import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';

import {
  TextField,
  Marquee,
  Tabs,
  Tab,
  List,
  ListItem,
  Button,
  Alert,
  Select,
} from 'bf-ui';

import styles from '../../../assets/styles/pages/deposit-withdraw.page.scss';

import {
  getDepositTexts_req,
  getPartnersByOperator_req,
  withdraw_req,
} from '../../../api/payment.api';

import websiteConfig from '../../../configs/websiteConfig';

import { getAvailableWithdraw_req } from '../../../api/user.api';

import { remainingMinSecs } from '../../../helpers/calculations.helper';
import DepositWithPartnerSection from '../deposit-with-partner/depositWithPartner.section';
import DepositWithKachaSection from '../deposit-with-kacha/depositWithKacha.section';
import DepositWithOtpSection from '../deposit-with-otp/depositWithOtp.section';

const tabsEnum = {
  DEPOSIT: 0,
  WITHDRAW: 1,
};

Object.freeze(tabsEnum);

const tempOperatorIds = {
  SANTIM: 1,
  KACHA: 3,
};

Object.freeze(tempOperatorIds);

export default function DepositWithdrawSection({ method }) {
  const router = useRouter();
  const intl = useIntl();

  const { operatorID } = useSelector((state) => state.user);
  const { operators } = useSelector((state) => state.global);

  const [partners, setPartners] = useState(null);
  const [activePartnerId, setActivePartnerId] = useState(null);

  const activePartner = useMemo(
    () => partners?.find((partner) => partner.id === activePartnerId),
    [partners, activePartnerId]
  );

  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');

  const processingTimerRef = useRef(0);

  const [remainingData, setRemainingData] = useState({
    processingTime: null,
    remainingText: '',
  });

  const [activeTab, setActiveTab] = useState(
    method === 0 ? tabsEnum.DEPOSIT : tabsEnum.WITHDRAW
  );

  const [activeOperatorId, setActiveOperatorId] = useState(null);

  const [marqueeText, setMarqueeText] = useState('');
  const [depositMessage, setDepositMessage] = useState('');

  const [amount, setAmount] = useState('');

  const [availableWithdrawAmount, setAvailableWithdrawAmount] = useState(null);

  const [withdrawLoading, setWithdrawLoading] = useState(false);

  const localAlertTimeoutRef = useRef(0);
  const [localAlertData, setLocalAlertData] = useState(null);

  const getMarqueeText = useCallback(async () => {
    try {
      const response = await getDepositTexts_req();

      if (response?.text) {
        setMarqueeText(response.text);
      }
    } catch (e) {
      console.error('DepositWithdrawPage -> getMarqueeText Error', e);
    }
  }, []);

  const getAvailableWithdraw = useCallback(async () => {
    try {
      const response = await getAvailableWithdraw_req();

      if (response?.amount !== undefined) {
        setAvailableWithdrawAmount(response.amount);
      }
    } catch (e) {
      console.error('DepositWithdrawPage -> getAvailableWithdraw Error', e);
    }
  }, []);

  const getActiveOperators = useCallback(() => {
    // console.log(operators);
    if (!operators?.length) {
      return [];
    }
    return operators.filter(
      (operator) =>
        (operator.State &&
          activeTab === tabsEnum.WITHDRAW &&
          operator.CanWithdraw) ||
        (operator.State && activeTab === tabsEnum.DEPOSIT && operator.CanPush)
    );
  }, [operators, activeTab]);

  const getActiveOperator = useCallback(() => {
    if (operators?.length && activeOperatorId) {
      return operators.find((data) => data.ID === activeOperatorId);
    }

    return null;
  }, [activeOperatorId, operators]);

  const isOperatorActive = useCallback(
    (operator) => operator.State,
    [operatorID, operators]
  );

  const onPartnerChange = useCallback(
    (e) => {
      const partnerData = partners.find(
        (partner) => partner.id === e.target.value
      );
      setAccountNumber(partnerData.accountNumber || '');
      setAccountName(partnerData.accountName || '');

      setActivePartnerId(e.target.value);
    },
    [partners]
  );

  const getPartnersByOperator = useCallback(async () => {
    try {
      if (!activeOperatorId) {
        return;
      }

      const data = await getPartnersByOperator_req({}, activeOperatorId);

      if (data) {
        setPartners(data);
        setActivePartnerId(data[0]?.id);

        if (data[0]?.accountNumber) {
          setAccountNumber(data[0].accountNumber);
        }

        if (data[0]?.accountName) {
          setAccountName(data[0].accountName);
        }
      }
    } catch (e) {
      console.log('DepositWithdrawPage -> getPartnersByOperator Error:', e);
    }
  }, [activeOperatorId]);

  useEffect(() => {
    if (operators && !activeOperatorId) {
      const activeOperators = getActiveOperators();

      if (activeOperators?.length) {
        setActiveOperatorId(activeOperators[0].ID);
      }
    }
  }, [operators]);

  useEffect(() => {
    getMarqueeText();
    getAvailableWithdraw();
  }, []);

  useEffect(() => {
    getPartnersByOperator();
  }, [activeOperatorId]);

  useEffect(() => {
    if (localAlertData) {
      localAlertTimeoutRef.current = setTimeout(() => {
        setLocalAlertData(null);
        setDepositMessage('');
      }, 7000);
    }
  }, [localAlertData]);

  const activeOperator = getActiveOperator();

  const onAmountChange = useCallback((e) => {
    setAmount(e.target.value);
  }, []);

  const onAccountNumberChange = useCallback(
    (e) => {
      if (e.target.value.length > activePartner.accountLength) {
        return;
      }
      setAccountNumber(e.target.value);
    },
    [activePartner]
  );

  const onAccountNameChange = useCallback((e) => {
    setAccountName(e.target.value);
  }, []);

  const onTabChange = useCallback(
    (tab) => {
      const url = tab === tabsEnum.DEPOSIT ? '/deposit' : '/withdraw';
      router.push(router.pathname, url, {
        shallow: true,
      });
      setActiveTab(tab);
      setLocalAlertData(null);
    },
    [router]
  );

  const withdraw = useCallback(async () => {
    if (withdrawLoading || !activeOperator) {
      return;
    }

    try {
      setWithdrawLoading(true);
      const response = await getAvailableWithdraw_req();

      if (amount > response?.amount || amount < 100) {
        setLocalAlertData({
          type: 'error',
          message: intl.formatMessage(
            {
              id: 'min_amount_max_amount',
            },
            {
              minAmount: 100,
              maxAmount: response.amount,
            }
          ),
        });

        return;
      }

      await withdraw_req(
        activeOperator.ID,
        Number(amount),
        activePartnerId,
        accountNumber,
        accountName
      );

      setLocalAlertData({
        type: 'success',
        message: intl.messages.withdrawal_processing,
      });
    } catch (e) {
      console.error('DepositWithdrawPage -> withdraw Error', e);
      setLocalAlertData({
        type: 'error',
        message: `Something went wrong! Please contact our support.`,
      });
    } finally {
      setWithdrawLoading(false);
    }
  }, [
    amount,
    withdrawLoading,
    activeOperator,
    accountNumber,
    accountName,
    activePartnerId,
  ]);

  const calculateRemainingTime = () => {
    if (!remainingData.processingTime) {
      return;
    }
    const remaining = remainingData.processingTime - Date.now();

    if (remaining >= 0) {
      const { minutes, seconds } = remainingMinSecs(remaining);

      processingTimerRef.current = setTimeout(() => {
        setRemainingData({
          processingTime: remainingData?.processingTime,
          remainingText: `${minutes}:${seconds}`,
        });
      }, 1000);
    } else {
      setRemainingData({
        processingTime: null,
        remainingText: '',
      });
      if (depositMessage) {
        setLocalAlertData({
          type: 'success',
          message: depositMessage,
        });
      }
    }
  };

  useEffect(() => {
    calculateRemainingTime();
  }, [remainingData]);

  const getDepositContent = () => {
    if (!activeOperator?.CanPush && !activeOperator?.CanUssd) {
      return <></>;
    }

    const pushContent = activeOperator?.CanPush ? (
      <div className="push_section">
        <h2 className="main_title">
          {intl.formatMessage(
            {
              id: 'method',
            },
            {
              num: 1,
            }
          )}
        </h2>
        <>
          {/* eslint-disable-next-line no-nested-ternary */}
          {activeOperator?.ID === tempOperatorIds.KACHA ? (
            <DepositWithKachaSection activeOperator={activeOperator} />
          ) : activeOperator?.WithOtp ? (
            <DepositWithOtpSection activeOperator={activeOperator} />
          ) : (
            <DepositWithPartnerSection activeOperator={activeOperator} />
          )}
        </>
      </div>
    ) : (
      <></>
    );

    const ussdContent =
      activeOperator?.CanUssd && activeOperator?.StepArr ? (
        <div className="ussd_section">
          <h2 className="main_title">
            {intl.formatMessage(
              {
                id: 'method',
              },
              {
                num: 2,
              }
            )}
          </h2>
          <List>
            {activeOperator.StepArr.map((data, index) => (
              <ListItem>
                <b>{index + 1}. </b>
                {data}
              </ListItem>
            ))}
          </List>
        </div>
      ) : (
        <></>
      );

    return (
      <>
        {pushContent}
        {ussdContent}
      </>
    );
  };

  // const getAmounts = useCallback(async () => {
  //   try {
  //     setAmounts(activeOperator?.DepositOptions || [1000, 2500, 15000, 50000]);
  //     setAmount(activeOperator?.SelectedDeposit || 2500);
  //   } catch (e) {
  //     console.error('DepositWithdrawSection -> getAmounts Error:', e);
  //     setAmounts([1000, 2500, 15000, 50000]);
  //     setAmount(2500);
  //   }
  // }, []);
  //
  // useEffect(() => {
  //   getAmounts();
  // }, []);

  const getWithdrawContent = () => {
    // if (!activeOperator?.CanWithdraw || !partners?.length || !activePartnerId) {
    if (!activeOperator?.CanWithdraw) {
      return <></>;
    }

    const pushContent = (
      <div className="push_section">
        <h2 className="main_title">
          {intl.formatMessage(
            {
              id: 'method',
            },
            {
              num: 1,
            }
          )}
        </h2>
        <div className="inputs_wrapper">
          {partners?.length ? (
            <div className="inputs_wrapper_top">
              <Select value={activePartnerId} onChange={onPartnerChange}>
                {partners?.map((data) => (
                  <option value={data.id}>{data.name}</option>
                ))}
              </Select>
              <TextField
                type="number"
                placeholder="Amount"
                value={amount}
                onChange={onAmountChange}
              />
            </div>
          ) : (
            <TextField
              type="number"
              placeholder="Amount"
              value={amount}
              onChange={onAmountChange}
            />
          )}

          {activePartner?.accountLength || activePartner?.accountName ? (
            <div className="inputs_wrapper_center">
              <TextField
                type="number"
                placeholder="Account Number"
                value={accountNumber}
                max={activePartner.accountLength}
                onChange={onAccountNumberChange}
              />
              <TextField
                type="text"
                placeholder="Account Name"
                value={accountName}
                onChange={onAccountNameChange}
              />
            </div>
          ) : (
            <></>
          )}
          <Button
            variant="contained"
            onClick={() => withdraw()}
            loading={withdrawLoading}
          >
            {intl.messages.withdraw}
          </Button>
        </div>
        <span className="available_withdraw">
          {intl.formatMessage(
            {
              id: 'available_withdraw_amount',
            },
            {
              amount: availableWithdrawAmount,
              currency: websiteConfig.currency,
            }
          )}
        </span>
        {localAlertData && (
          <>
            <br />
            <Alert
              severity={localAlertData?.type}
              title={
                localAlertData?.type === 'success'
                  ? intl.messages.success
                  : intl.messages.error
              }
            >
              {localAlertData?.message}
            </Alert>
          </>
        )}
      </div>
    );

    return <>{pushContent}</>;
  };

  const getContent = () => {
    if (activeTab === tabsEnum.DEPOSIT) {
      return getDepositContent();
    }

    if (activeTab === tabsEnum.WITHDRAW) {
      return getWithdrawContent();
    }

    return <></>;
  };

  return (
    <>
      <div id="deposit-withdraw-page" className="page">
        <div className="page_inner">
          {marqueeText && <Marquee>{marqueeText}</Marquee>}
          <Tabs slider value={activeTab} onChange={onTabChange}>
            <Tab>
              <span>{intl.messages.deposit}</span>
            </Tab>
            <Tab>
              <span>{intl.messages.withdraw}</span>
            </Tab>
          </Tabs>
          <div className="content">
            <div className="operators_wrapper">
              {getActiveOperators().map((data) => (
                <div
                  key={data.ID}
                  className={classnames(
                    'operator',
                    data.ID === activeOperatorId ? 'active' : ''
                    // data.ID !== operatorID ? 'disabled' : ''
                  )}
                  onClick={() => {
                    if (isOperatorActive(data)) {
                      setActiveOperatorId(data.ID);
                    }
                  }}
                >
                  <img
                    src={`${websiteConfig.cdnUrl}api/v1/media/${data.MediaID}`}
                    alt={data.Name}
                  />
                </div>
              ))}
            </div>
            {activeOperator ? (
              <div className="content_inner">{getContent()}</div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
      <style jsx>{styles}</style>
    </>
  );
}
